import { Info, BarChart3, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { productApi } from "@/resources/product";
import { useAppData } from "@/contexts/AppDataContext";
import { resolveDateRange } from "@/utils";
import { useMemo } from "react";
import type { BrandSummaryResponse } from "@/types";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { MetricCards } from "./MetricCards";
import { CorrelationChart } from "./CorrelationChart";
import { buildSwitchMarkers } from "./utils";

interface BrandCorrelationSummaryProps {
  selectedAsins?: number[];
  selectedDays?: number;
  onDaysChange?: (days: number) => void;
  customDateRange?: { from: Date | undefined; to: Date | undefined };
  onCustomDateChange?: (range: { from: Date | undefined; to: Date | undefined }) => void;
  publicData?: BrandSummaryResponse | null;
  cardClassName?: string;
  showMetricCards?: boolean;
  onViewTest?: (testName: string) => void;
}

export function BrandCorrelationSummary({
  selectedAsins,
  selectedDays = 30,
  onDaysChange,
  customDateRange = { from: undefined, to: undefined },
  onCustomDateChange,
  publicData,
  cardClassName = "rounded-t-sm border border-b-0 bg-card",
  showMetricCards = true,
  onViewTest,
}: BrandCorrelationSummaryProps) {
  const isPublic = publicData !== undefined;
  const { selectedBrandId } = useAppData();
  const { dateFrom, dateTo } = useMemo(
    () => resolveDateRange(selectedDays, customDateRange),
    [selectedDays, customDateRange],
  );
  const { data: fetchedSummary, isLoading, isPlaceholderData } = rankTrackerApi.useGetBrandSummary(
    isPublic ? null : selectedBrandId,
    dateFrom,
    dateTo,
    selectedAsins,
  );
  const { data: heroAbTests } = productApi.useGetBrandHeroAbTests(
    isPublic ? null : selectedBrandId,
  );

  const summary = isPublic ? publicData : fetchedSummary;
  const isFetchingFilter = !isPublic && (isLoading || isPlaceholderData);
  const chartReadyData = useMemo(
    () =>
      (summary?.data ?? []).map((d) => ({
        ...d,
        avgRank: d.avgRank != null && d.avgRank > 0 ? d.avgRank : null,
      })),
    [summary?.data],
  );

  const switchMarkers = useMemo(
    () => buildSwitchMarkers(chartReadyData, heroAbTests ?? []),
    [chartReadyData, heroAbTests],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        <div className={cardClassName}>
          <div className="p-6 pb-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                Brand Sales Correlation
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs leading-relaxed">
                    <p>Aggregated view of how keyword rank improvements across all tracked keywords correlate with sales performance over a 30-day rolling window.</p>
                    <p className="mt-1">Uses Pearson correlation (r) to quantify the rank–sales relationship.</p>
                  </TooltipContent>
                </Tooltip>
              </h3>
              {!isPublic && onDaysChange && onCustomDateChange && (
                <DateRangePicker
                  selectedDays={selectedDays}
                  onDaysChange={onDaysChange}
                  customDateRange={customDateRange}
                  onCustomDateChange={onCustomDateChange}
                />
              )}
            </div>
            {summary && !isFetchingFilter && (
              <p className="text-sm text-muted-foreground mt-2 mb-1">
                {summary.selectedHeroAsins} of {summary.totalHeroAsins} Hero ASINs · {summary.totalKeywords} keyword{summary.totalKeywords === 1 ? "" : "s"} · {summary.windowLabel}
              </p>
            )}
          </div>
          <div className="px-6 pb-4">
            {isFetchingFilter ? (
              <div className="h-[280px] w-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Loading…</p>
                </div>
              </div>
            ) : !summary || (summary.data || []).length === 0 ? (
              <div className="h-[280px] w-full flex items-center justify-center rounded-lg border border-dashed border-border">
                <div className="text-center space-y-2">
                  <BarChart3 className="h-10 w-10 text-muted-foreground/30 mx-auto" />
                  <p className="text-sm text-muted-foreground">No rank snapshots yet for this window</p>
                  <p className="text-xs text-muted-foreground/70">Data will appear here once daily snapshots are captured</p>
                </div>
              </div>
            ) : (
              <>
                {showMetricCards && <MetricCards summary={summary} />}
                <div className="flex items-center text-xs font-semibold mb-0.5">
                  <span style={{ width: 32, textAlign: "center", color: "hsl(var(--primary))" }}>Avg Brand Rank</span>
                  <span style={{ width: 40, textAlign: "center", color: "hsl(var(--chart-5))" }}>BSR</span>
                  <span className="ml-auto text-muted-foreground">Units Sold</span>
                </div>
                <CorrelationChart
                  data={chartReadyData}
                  markers={switchMarkers}
                  onViewTest={onViewTest}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
